import { ResearchCover } from '../components/sections/ResearchCover';
import { getResearchPdfCover } from '../services/researchCover';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  Download,
  ExternalLink,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { Container, Button } from '../components/ui';
import {
  detectMediaKind,
  fetchResearchEntries,
  getIndustries,
  type ResearchEntry,
} from '../services/researchApi';
import { NotFound } from './NotFound';

/*
 * ResearchDetail
 * --------------
 * Detail page for a single "Research in practice" entry, rendered at
 * `/research/<sorting_id-or-id>`.
 *
 * Because the catalog is tiny we still avoid a dedicated
 * `GET /in_practice_research/<id>` endpoint — the FE fetches the
 * full list on mount and resolves locally by matching either
 * `sorting_id.toString()` OR the stringified Mongo `id`.
 *
 * Page layout (top → bottom)
 * --------------------------
 *   1. Sticky breadcrumb bar — `Home > IstaLabs > <entry header>`.
 *      Both "Home" and "IstaLabs" are clickable Links so the visitor
 *      can bounce back to either level in one tap.
 *
 *   2. Header cover strip — a moderate-height navy banner that
 *      displays the entry's `cover_image_url` (a wordmark PNG). The
 *      image is filter-inverted to white so black-on-transparent
 *      wordmarks (like Doceract) read cleanly on navy.
 *
 *   3. Split content section — video on the LEFT, description +
 *      chips + CTAs on the RIGHT. The intro video autoplays WITH
 *      SOUND on load; if the browser blocks unmuted autoplay we
 *      gracefully fall back to muted playback and surface an
 *      "Unmute" button so the visitor can turn sound on with a
 *      single click.
 *
 *   4. Quiet back-navigation strip so a visitor who's read to the
 *      end can either return to the IstaLabs landing or route into
 *      the contact form to talk to the team about the piece.
 *
 * Autoplay-with-sound note
 * ------------------------
 * Modern browsers block unmuted autoplay unless the tab has
 * "user activation" — a recent click / tap in the same document.
 * Because the visitor lands here via a React Router `<Link>` click
 * (not a full page reload), the user-activation flag typically
 * carries over and unmuted autoplay just works. When it doesn't
 * (Firefox strict mode, some mobile configs), the `.play()` promise
 * rejects; our try/catch falls back to muted and reveals a small
 * unmute button in the top-right corner of the video frame.
 */

const HEADER_OFFSET_PX = 80; // matches Header's h-20

const LOADING_PLACEHOLDER_MIN_HEIGHT = 480;

export const ResearchDetail: React.FC = () => {
  const { entryId } = useParams<{ entryId: string }>();
  const [entries, setEntries] = useState<ResearchEntry[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await fetchResearchEntries();
      if (!cancelled) setEntries(list);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Resolve `:entryId` against BOTH the numeric `sorting_id` and the
  // stringified Mongo `_id` so clean numeric URLs (`/research/10`)
  // work, and stable Mongo-id URLs also work as a fallback.
  const entry = entries?.find(
    (e) =>
      (typeof e.sorting_id === 'number' && String(e.sorting_id) === entryId) ||
      e.id === entryId,
  );

  // Still loading — render a lightweight placeholder so the page
  // doesn't flash "not found" for a fraction of a second while the
  // fetch is in flight.
  if (entries === null) {
    return (
      <>
        <div style={{ height: `${HEADER_OFFSET_PX}px` }} aria-hidden />
        <div
          className="flex items-center justify-center bg-white"
          style={{ minHeight: `${LOADING_PLACEHOLDER_MIN_HEIGHT}px` }}
        >
          <p className="text-navy-400 text-sm">Loading research entry…</p>
        </div>
      </>
    );
  }

  // Fetched but no matching row — render the site-wide 404 inline
  // (same pattern as `IndustryDetail`) so the URL bar stays honest.
  if (!entry) return <NotFound />;

  return (
    <>
      {/* ═════════════════ Breadcrumbs strip ═════════════════ */}
      <div style={{ height: `${HEADER_OFFSET_PX}px` }} aria-hidden />
      <div className="sticky top-20 z-40 bg-white border-b border-navy-100 shadow-sm">
        <Container className="py-4">
          <Breadcrumbs headerLabel={entry.header ?? 'Research entry'} />
        </Container>
      </div>

      {/* ═════════════════ Header cover strip ═════════════════ */}
      <HeaderCoverStrip entry={entry} />

      {/* ═════════════════ Split section (video ← | → description) ═════════════════ */}
      <SplitContentSection entry={entry} />

      {/* ═════════════════ Back-navigation strip ═════════════════ */}
      <BackNavigationSection />
    </>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Sticky breadcrumb trail. Both "Home" and "IstaLabs" are clickable
 * so a visitor who wants to jump to either level does it in one
 * tap. The current entry title is the (unlinked) trailing crumb.
 */
const Breadcrumbs: React.FC<{ headerLabel: string }> = ({ headerLabel }) => (
  <nav
    aria-label="Breadcrumb"
    className="flex flex-wrap items-center gap-2 text-sm"
  >
    <Link
      to="/"
      className="text-navy-500 hover:text-accent-600 transition-colors"
    >
      Home
    </Link>
    <ChevronRight className="w-3.5 h-3.5 text-navy-400 shrink-0" aria-hidden />
    <Link
      to="/istalabs"
      className="text-navy-500 hover:text-accent-600 transition-colors"
    >
      IstaLabs
    </Link>
    <ChevronRight className="w-3.5 h-3.5 text-navy-400 shrink-0" aria-hidden />
    <span aria-current="page" className="font-semibold text-navy-900 line-clamp-1">
      {headerLabel}
    </span>
  </nav>
);

/**
 * Header banner for the entry's `cover_image_url`. Moderate height
 * so the substantive split section below is visible on first paint.
 *
 * Renders the right element for the media kind:
 *   • image / gif → <img> with the `invert brightness-0` wordmark
 *                    filter (black-on-transparent wordmark → white
 *                    on navy). GIFs auto-animate.
 *   • video       → <video autoplay muted loop> at the same height
 *                    cap. No colour filter — video assets ship with
 *                    their own visual treatment; inverting them
 *                    would butcher any non-monochrome frame.
 *
 * Missing or broken covers fall back to a registered PDF first page,
 * preserving the original document colours.
 *
 * Videos in this slot deliberately play MUTED because the audible
 * intro clip lives in the split section below; two autoplaying
 * audio sources on one page is a UX bug, not a feature.
 */
const HeaderCoverStrip: React.FC<{ entry: ResearchEntry }> = ({ entry }) => (
  <section className="relative bg-navy-950 overflow-hidden">
    <div className="w-full flex items-center justify-center px-6 py-10 md:py-14 lg:py-16 min-h-[220px] md:min-h-[280px]">
      <ResearchCover entry={entry} className="max-h-[140px] md:max-h-[180px] lg:max-h-[220px] w-auto max-w-full object-contain" />
    </div>
  </section>
);

/**
 * Split content — the substantive body of the page.
 *
 *   LEFT column  — intro video, autoplays WITH SOUND on mount. Falls
 *                  back to muted playback with an unmute affordance
 *                  if the browser blocks unmuted autoplay. Also
 *                  renders full native controls so users can pause,
 *                  scrub, or adjust volume manually.
 *
 *   RIGHT column — chips (categories + industries), the entry's
 *                  header, its long-form description, an optional
 *                  "touches these services" footer, and two primary
 *                  CTAs (Download documentation + Explore website).
 *
 * On <md the columns stack (video on top, text below) — the copy is
 * long enough that reading it under the video works fine on phones.
 */
const SplitContentSection: React.FC<{ entry: ResearchEntry }> = ({ entry }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // `true` when the video is currently playing without sound. Set
  // if unmuted autoplay was blocked by the browser and we had to
  // fall back to muted — surfaces the "Unmute" button below.
  const [fellBackToMuted, setFellBackToMuted] = useState(false);

  // Left-column media can be a real video OR a static image / GIF
  // (content editors sometimes use the `intro_video_url` slot for a
  // wordmark or a walkthrough screenshot). Detect once here and
  // branch the render below — the autoplay-with-sound handshake
  // only applies to real videos; images render as-is.
  const introMediaKind = detectMediaKind(entry.intro_video_url);
  const introIsVideo =
    Boolean(entry.intro_video_url) && introMediaKind === 'video';

  // Autoplay-with-sound handshake. Tries unmuted first; on rejection
  // (browser policy) falls back to muted playback so the video still
  // starts, and exposes an unmute button via `fellBackToMuted`.
  // NOTE: only runs when the intro asset is a real video — for a
  // static image / GIF the effect is a no-op below (the `<video>`
  // element isn't in the DOM to reference).
  useEffect(() => {
    if (!introIsVideo) return;
    const v = videoRef.current;
    if (!v || !entry.intro_video_url) return;
    v.muted = false;
    const p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        v.muted = true;
        setFellBackToMuted(true);
        v.play().catch(() => {
          // Even muted autoplay was refused. Give up silently; the
          // user can still hit the native play control themselves.
        });
      });
    }
  }, [entry.intro_video_url, introIsVideo]);

  const handleUnmute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    // Nudge play() again because some engines pause a video when its
    // muted state is toggled mid-playback.
    v.play().catch(() => {});
    setFellBackToMuted(false);
  }, []);

  const industries = getIndustries(entry);
  const serviceCategories = entry.service_categories ?? [];

  return (
    <section className="bg-white py-14 md:py-20 lg:py-24 overflow-x-clip">
      {/* Widened wrapper — the default `<Container>` (`max-w-7xl` =
          1280 px) capped the video at ~330 px tall on 1440 px
          viewports. Using a full-bleed wrapper with responsive
          horizontal padding gives the whole split section ~1360 px
          of usable width on desktop, which lets the video breathe
          without stealing space from the copy on the right. */}
      <div className="site-wide-container">
        {/*
          Asymmetric split — video column is deliberately ~57%
          (`1.35fr` / `1fr`) so the video reads as the primary
          media on the page, not a small thumbnail next to a
          paragraph. On <md the columns stack (video on top,
          copy below) so mobile is unaffected.
        */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] gap-8 md:gap-10 lg:gap-14 items-start">
          {/* ─── LEFT: video (autoplays with sound) ─── */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            {entry.intro_video_url && introIsVideo ? (
              // ── Real video (webm / mp4 / mov / …): the flagship
              //    case. Autoplays WITH SOUND, falls back to muted
              //    with an unmute button if the browser blocks.
              //    `aspect-video` (16:9) keeps the frame in step
              //    with the source clip; the extra column width
              //    from the 1.35fr split above translates directly
              //    into ~440 px of video height on 1440 px viewports.
              <div className="relative rounded-2xl overflow-hidden bg-navy-950 shadow-[0_25px_70px_-25px_rgba(2,8,20,0.5)] aspect-video">
                <video
                  ref={videoRef}
                  key={entry.intro_video_url}
                  src={entry.intro_video_url}
                  autoPlay
                  loop
                  playsInline
                  // Native controls give the visitor a reliable way
                  // to pause, scrub, and adjust sound if our own
                  // autoplay-with-sound handshake got overridden.
                  controls
                  preload="auto"
                  className="w-full h-full object-cover"
                />
                {/* Unmute nudge — only appears if we had to fall
                    back to muted autoplay because the browser
                    blocked sound. Tapping it turns audio back on. */}
                {fellBackToMuted && (
                  <button
                    type="button"
                    onClick={handleUnmute}
                    aria-label="Unmute video"
                    className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-navy-950/85 text-white px-3 py-1.5 text-xs font-semibold ring-1 ring-white/20 hover:bg-navy-950 shadow-md"
                  >
                    <VolumeX className="w-3.5 h-3.5" />
                    Tap to unmute
                  </button>
                )}
                {/* Subtle "playing" hint — small volume glyph in the
                    corner while sound is on, so the visitor knows
                    they're hearing the audio deliberately. */}
                {!fellBackToMuted && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-black/40 text-white/85 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider"
                  >
                    <Volume2 className="w-3 h-3" />
                    Sound on
                  </span>
                )}
              </div>
            ) : entry.intro_video_url ? (
              // ── Static image or animated GIF in the intro slot.
              //    Rendered as <img>: GIFs auto-animate, still
              //    images sit static. `object-contain` letterboxes
              //    the media on the navy backdrop so nothing is
              //    cropped — the intro slot should show the whole
              //    asset the editor chose. No wordmark-invert
              //    filter here (unlike the header cover strip) —
              //    the intro slot is a general-purpose media well,
              //    not a monochrome-wordmark treatment.
              <div className="relative rounded-2xl overflow-hidden bg-navy-950 shadow-[0_25px_70px_-25px_rgba(2,8,20,0.5)] aspect-video flex items-center justify-center p-4">
                <img
                  src={entry.intro_video_url}
                  alt={entry.header ?? 'Research entry intro'}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            ) : (entry.cover_image_url || getResearchPdfCover(entry.research_documentation_url)) ? (
              <div className="relative rounded-2xl overflow-hidden bg-navy-950 aspect-video flex items-center justify-center p-4">
                <ResearchCover entry={entry} className="max-h-full max-w-full object-contain" />
              </div>
            ) : null}
          </motion.div>

          {/* ─── RIGHT: chips + header + description + CTAs ─── */}
          <motion.div
            className="flex flex-col"
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{
              duration: 0.5,
              delay: 0.05,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            {/* Chip cluster: research/implementation + industries */}
            {(entry.categories?.length || industries.length > 0) ? (
              <div className="flex flex-wrap items-center gap-2 mb-5">
                {(entry.categories ?? []).map((cat) => (
                  <span
                    key={cat}
                    className="inline-flex items-center rounded-full bg-accent-50 text-accent-700 text-[11px] font-semibold uppercase tracking-wider px-3 py-1 ring-1 ring-accent-200"
                  >
                    {cat}
                  </span>
                ))}
                {industries.map((ind) => (
                  <span
                    key={ind}
                    className="inline-flex items-center rounded-full bg-navy-50 text-navy-700 text-[11px] font-semibold uppercase tracking-wider px-3 py-1 ring-1 ring-navy-200"
                  >
                    {ind}
                  </span>
                ))}
              </div>
            ) : null}

            {entry.header && (
              <h1 className="text-2xl md:text-3xl lg:text-[36px] font-bold text-navy-950 leading-tight mb-4 md:mb-5">
                {entry.header}
              </h1>
            )}

            {entry.description && (
              <p className="text-base md:text-lg text-navy-700 leading-relaxed whitespace-pre-line mb-6">
                {entry.description}
              </p>
            )}

            {/* Quiet secondary metadata — which of our services this
                touches. Rendered as inline copy rather than another
                chip cluster so it reads as "here's what area of the
                practice this proves" rather than as more tags. */}
            {serviceCategories.length > 0 && (
              <p className="text-sm text-navy-500 mb-8">
                Touches:{' '}
                <span className="text-navy-800 font-semibold">
                  {serviceCategories.join(' · ')}
                </span>
              </p>
            )}

            {/* CTAs — Download documentation + Explore the site.
                Each button only renders when its underlying URL
                exists so a partially-authored row doesn't show
                dead buttons. */}
            <div className="flex flex-wrap items-center gap-3">
              {entry.research_documentation_url && (
                <a
                  href={entry.research_documentation_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  // The `download` hint asks browsers to save
                  // rather than preview; browsers that ignore it
                  // still open the PDF in a new tab which is fine.
                  download
                >
                  <Button
                    variant="primary"
                    size="lg"
                    rightIcon={<Download className="w-5 h-5" />}
                  >
                    Download documentation
                  </Button>
                </a>
              )}
              {entry.website_url && (
                <a
                  href={entry.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    variant="outline"
                    size="lg"
                    rightIcon={<ExternalLink className="w-5 h-5" />}
                  >
                    Explore the practice
                  </Button>
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/**
 * Quiet back-navigation strip at the bottom of the page. Kept
 * intentionally understated (no CTA button styling) because the
 * primary CTAs live above and this is just a return path for
 * visitors who scrolled to the very end.
 */
const BackNavigationSection: React.FC = () => (
  <section className="bg-navy-50/60 border-t border-navy-100 py-8 md:py-10">
    <Container>
      <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
        <Link
          to="/istalabs"
          className="inline-flex items-center gap-2 text-navy-700 hover:text-accent-700 font-semibold"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to IstaLabs
        </Link>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 text-navy-700 hover:text-accent-700 font-semibold"
        >
          Talk to the team about this
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </Container>
  </section>
);
